import { useEffect, useRef, useState } from 'react'

interface ConstellationSplashProps {
  /**
   * While true, holds on the formed wordmark. When it flips to false and
   * the minimum hold has elapsed, the splash begins its two-stage fade and
   * eventually calls `onFinish`.
   */
  waitForReady: boolean
  onFinish: () => void
  /** Word to form. Defaults to the brand mark. */
  word?: string
  /** Fires once the wordmark stars have finished settling. */
  onFormed?: () => void
  /** Animation timing override. Defaults to the fast console boot profile. */
  timing?: Partial<ConstellationSplashTiming>
}

export type ConstellationSplashTiming = {
  enterMs: number
  formMs: number
  holdMs: number
  fadeBgMs: number
  fadeDriftMs: number
}

const DEFAULT_CONSTELLATION_SPLASH_TIMING: ConstellationSplashTiming = {
  enterMs: 80,
  formMs: 1000,
  holdMs: 200,
  fadeBgMs: 140,
  fadeDriftMs: 80,
}

/** First fade stage — backdrop dims to transparent, wordmark stars fade. */
/** Second fade stage — ambient drift stars finally fade, over the visible app. */

/** Approximate spacing between wordmark stars (in CSS px). Smaller = denser
 *  and more prominent. */
const WORDMARK_STAR_SPACING = 9
/** How many pure-drift stars sit on top of the forming set. */
const DRIFT_STARS = 220

type Phase = 'entering' | 'forming' | 'settled' | 'fadingBg' | 'fadingDrift'

interface Dot {
  cx: number
  cy: number
  vx: number
  vy: number
  role: 'drift' | 'form'
  sx: number
  sy: number
  tx: number
  ty: number
  hue: number
}

/**
 * Full-screen constellation splash. A network of stars drifts across the
 * viewport while a subset settles into positions that spell the wordmark.
 * On exit the backdrop + wordmark stars fade first (revealing the app),
 * then the drift network fades on top of the visible UI.
 */
export const ConstellationSplash = ({
  waitForReady,
  onFinish,
  word = 'Timelining',
  onFormed,
  timing,
}: ConstellationSplashProps) => {
  const resolvedTiming = { ...DEFAULT_CONSTELLATION_SPLASH_TIMING, ...timing }
  const timingRef = useRef(resolvedTiming)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const phaseRef = useRef<Phase>('entering')
  const [phase, setPhaseState] = useState<Phase>('entering')
  const finishedRef = useRef(false)
  const minMetRef = useRef(false)
  const fadeBgStartRef = useRef<number | null>(null)
  const fadeDriftStartRef = useRef<number | null>(null)
  const waitForReadyRef = useRef(waitForReady)

  useEffect(() => {
    timingRef.current = resolvedTiming
    waitForReadyRef.current = waitForReady
  })

  const setPhase = (next: Phase) => {
    phaseRef.current = next
    setPhaseState(next)
  }

  const beginFade = () => {
    if (!minMetRef.current || waitForReadyRef.current || finishedRef.current) return
    finishedRef.current = true
    fadeBgStartRef.current = performance.now()
    setPhase('fadingBg')
    window.setTimeout(() => {
      fadeDriftStartRef.current = performance.now()
      setPhase('fadingDrift')
      window.setTimeout(onFinish, timingRef.current.fadeDriftMs)
    }, timingRef.current.fadeBgMs)
  }

  useEffect(() => {
    const { enterMs, formMs, holdMs } = timingRef.current
    const t1 = window.setTimeout(() => setPhase('forming'), enterMs)
    const t2 = window.setTimeout(() => {
      setPhase('settled')
      onFormed?.()
    }, enterMs + formMs)
    const t3 = window.setTimeout(() => {
      minMetRef.current = true
      beginFade()
    }, enterMs + formMs + holdMs)
    return () => {
      window.clearTimeout(t1)
      window.clearTimeout(t2)
      window.clearTimeout(t3)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!waitForReady) beginFade()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waitForReady])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const dpr = window.devicePixelRatio || 1
    let raf = 0
    let running = true
    let dots: Dot[] = []
    let cssW = 0
    let cssH = 0
    let formingStartTime = 0
    let lastPhaseSeen: Phase | null = null

    const sampleTextPositions = (w: number, h: number): { x: number; y: number }[] => {
      const off = document.createElement('canvas')
      off.width = Math.floor(w * dpr)
      off.height = Math.floor(h * dpr)
      const octx = off.getContext('2d')
      if (!octx) return []
      octx.scale(dpr, dpr)
      octx.fillStyle = 'white'
      const fontSize = Math.min(h * 0.32, (w * 0.9) / (word.length * 0.55))
      octx.font = `italic 700 ${fontSize}px "Playfair Display", "Georgia", "Times New Roman", serif`
      octx.textAlign = 'center'
      octx.textBaseline = 'middle'
      octx.fillText(word, w / 2, h / 2)
      const imageData = octx.getImageData(0, 0, off.width, off.height)
      const data = imageData.data
      const stepPx = WORDMARK_STAR_SPACING
      const stepDev = Math.max(2, Math.floor(stepPx * dpr))
      const positions: { x: number; y: number }[] = []
      for (let y = 0; y < off.height; y += stepDev) {
        for (let x = 0; x < off.width; x += stepDev) {
          const alpha = data[(y * off.width + x) * 4 + 3]
          if (alpha > 128) {
            positions.push({ x: x / dpr, y: y / dpr })
          }
        }
      }
      return positions
    }

    const makeDriftDot = (w: number, h: number): Dot => ({
      cx: Math.random() * w,
      cy: Math.random() * h,
      vx: (Math.random() - 0.5) * 0.18,
      vy: (Math.random() - 0.5) * 0.18,
      role: 'drift',
      sx: 0,
      sy: 0,
      tx: 0,
      ty: 0,
      hue: Math.random(),
    })

    const buildDots = (w: number, h: number) => {
      const targets = sampleTextPositions(w, h)
      const formingDots: Dot[] = targets.map((t) => ({
        cx: Math.random() * w,
        cy: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.18,
        vy: (Math.random() - 0.5) * 0.18,
        role: 'form',
        sx: 0,
        sy: 0,
        tx: t.x,
        ty: t.y,
        hue: Math.random(),
      }))
      const driftDots: Dot[] = Array.from({ length: DRIFT_STARS }, () => makeDriftDot(w, h))
      dots = [...driftDots, ...formingDots]
    }

    const fit = (w: number, h: number) => {
      if (w < 1 || h < 1) return
      cssW = w
      cssH = h
      canvas.width = Math.floor(w * dpr)
      canvas.height = Math.floor(h * dpr)
      canvas.style.width = `${w}px`
      canvas.style.height = `${h}px`
      ctx.setTransform(1, 0, 0, 1, 0, 0)
      ctx.scale(dpr, dpr)
      if (dots.length === 0) buildDots(w, h)
    }

    const easeInOutCubic = (t: number) =>
      t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2

    const stepDrift = (d: Dot) => {
      d.cx += d.vx
      d.cy += d.vy
      if (d.cx < 0 || d.cx > cssW) d.vx *= -1
      if (d.cy < 0 || d.cy > cssH) d.vy *= -1
    }

    const tick = () => {
      if (!running) return
      const currentPhase = phaseRef.current

      if (currentPhase !== lastPhaseSeen) {
        if (currentPhase === 'forming') {
          for (const d of dots) {
            if (d.role === 'form') {
              d.sx = d.cx
              d.sy = d.cy
            }
          }
          formingStartTime = performance.now()
        }
        lastPhaseSeen = currentPhase
      }

      ctx.clearRect(0, 0, cssW, cssH)

      const now = performance.now()
      const formingT = currentPhase === 'forming'
        ? easeInOutCubic(Math.min(1, (now - formingStartTime) / timingRef.current.formMs))
        : currentPhase === 'entering' ? 0 : 1

      for (const d of dots) {
        if (d.role === 'drift') {
          stepDrift(d)
        } else if (currentPhase === 'entering') {
          stepDrift(d)
        } else if (currentPhase === 'forming') {
          d.cx = d.sx + (d.tx - d.sx) * formingT
          d.cy = d.sy + (d.ty - d.sy) * formingT
        } else {
          // settled + both fade phases — gentle idle hover at target
          d.cx = d.tx + Math.sin(now / 1400 + d.hue * 6.28) * 0.6
          d.cy = d.ty + Math.cos(now / 1600 + d.hue * 6.28) * 0.6
        }
      }

      // Fade alphas: wordmark ramps down during fadingBg; drift ramps down
      // during fadingDrift.
      let wordmarkAlpha = 1
      let driftAlpha = 1
      if (currentPhase === 'fadingBg') {
        const started = fadeBgStartRef.current ?? now
        const t = Math.min(1, (now - started) / timingRef.current.fadeBgMs)
        wordmarkAlpha = 1 - t
      } else if (currentPhase === 'fadingDrift') {
        wordmarkAlpha = 0
        const started = fadeDriftStartRef.current ?? now
        const t = Math.min(1, (now - started) / timingRef.current.fadeDriftMs)
        driftAlpha = 1 - t
      }

      // Links between drifting stars — carry the drift alpha.
      if (driftAlpha > 0.01) {
        const linkDist = 110
        const linkDist2 = linkDist * linkDist
        ctx.lineWidth = 0.7
        for (let i = 0; i < dots.length; i++) {
          if (dots[i].role !== 'drift') continue
          for (let j = i + 1; j < dots.length; j++) {
            if (dots[j].role !== 'drift') continue
            const dx = dots[i].cx - dots[j].cx
            const dy = dots[i].cy - dots[j].cy
            const d2 = dx * dx + dy * dy
            if (d2 < linkDist2) {
              const alpha = (1 - d2 / linkDist2) * 0.45 * driftAlpha
              ctx.strokeStyle = `rgba(150, 135, 230, ${alpha})` // mosaic-keep: canvas splash connecting links graphic art
              ctx.beginPath()
              ctx.moveTo(dots[i].cx, dots[i].cy)
              ctx.lineTo(dots[j].cx, dots[j].cy)
              ctx.stroke()
            }
          }
        }
      }

      // Dots — same colour + radius everywhere, only alpha differs by role
      // during fade so the wordmark leaves first.
      for (const d of dots) {
        const a = (d.role === 'form' ? wordmarkAlpha : driftAlpha) * 0.85
        if (a <= 0.01) continue
        ctx.fillStyle = `rgba(200, 195, 235, ${a})` // mosaic-keep: canvas splash star particles graphic art
        ctx.beginPath()
        ctx.arc(d.cx, d.cy, 1.4, 0, Math.PI * 2)
        ctx.fill()
      }

      raf = requestAnimationFrame(tick)
    }

    const ro = new ResizeObserver((entries) => {
      const box = entries[0]?.contentRect
      if (box) fit(box.width, box.height)
    })
    ro.observe(canvas)
    const rect = canvas.getBoundingClientRect()
    fit(rect.width, rect.height)
    tick()

    return () => {
      running = false
      cancelAnimationFrame(raf)
      ro.disconnect()
    }
  }, [word])

  return (
    <div
      className={`constellationSplash constellationSplash--${phase}`}
      role="status"
      aria-live="polite"
      aria-label={word}
    >
      <div className="constellationSplash__backdrop" aria-hidden="true" />
      <canvas ref={canvasRef} className="constellationSplash__canvas" aria-hidden="true" />
    </div>
  )
}
